/**
 * Which messages belong to one conversation.
 *
 * There is no Sent box: a reply is shown under the message it answers. The
 * grouping is the mail's own threading headers — `Message-Id`, `In-Reply-To`
 * and `References` — joined transitively, so a message that names any id
 * another message names is in the same conversation.
 *
 * Subject matching is deliberately absent. It joins conversations that are not
 * related, and two people answering the same newsletter is not a thread.
 */

export interface Threadable {
  /** Gift wrap id: unique, and the only id every message is sure to have. */
  id: string;
  message_id?: string;
  in_reply_to?: string;
  references?: string[];
  dateMs: number;
  sent?: boolean;
}

/** A Message-Id as both sides write it: the angle brackets are not part of it. */
export function bareId(value: string | undefined): string {
  return (value ?? "").trim().replace(/^<+|>+$/g, "");
}

/** Every threading id one message states, in no order. */
function idsOf(item: Threadable): string[] {
  const all = [item.message_id, item.in_reply_to, ...(item.references ?? [])];
  return [...new Set(all.map(bareId).filter(Boolean))];
}

/**
 * Group every message into conversations.
 *
 * The key is opaque and stable for one call only. Each list is oldest first,
 * which is reading order: the message, then what answered it.
 */
export function conversations<T extends Threadable>(items: readonly T[]): Map<string, T[]> {
  const parent = new Map<string, string>();

  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) !== undefined && parent.get(root) !== root) {
      root = parent.get(root)!;
    }
    parent.set(id, root);
    return root;
  };
  const join = (a: string, b: string): void => {
    const [ra, rb] = [find(a), find(b)];
    if (ra !== rb) parent.set(rb, ra);
  };

  for (const item of items) {
    const ids = idsOf(item);
    // A message with no threading headers at all is its own conversation, and
    // the wrap id is the one name it cannot share with anything else.
    const keys = ids.length > 0 ? ids : [`wrap:${item.id}`];
    for (const id of keys) if (!parent.has(id)) parent.set(id, id);
    for (const id of keys.slice(1)) join(keys[0]!, id);
  }

  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const ids = idsOf(item);
    const key = find(ids[0] ?? `wrap:${item.id}`);
    const list = grouped.get(key);
    if (list) list.push(item);
    else grouped.set(key, [item]);
  }
  for (const list of grouped.values()) list.sort(order);
  return grouped;
}

/**
 * The conversation one message belongs to, oldest first.
 *
 * The message itself is always in the answer, so a caller never has to decide
 * whether an unthreaded message has a conversation.
 */
export function conversationOf<T extends Threadable>(items: readonly T[], id: string): T[] {
  const item = items.find((m) => m.id === id);
  if (!item) return [];
  for (const list of conversations(items).values()) {
    if (list.some((m) => m.id === id)) return list;
  }
  return [item];
}

/**
 * True when this message this account sent is not an answer to anything.
 *
 * A reply belongs under the message it answers and nowhere else. A message that
 * starts a conversation has nothing to sit under, so it is a card of its own,
 * marked as yours — otherwise mail written here would be invisible.
 */
export function startsConversation(item: Threadable): boolean {
  return (
    item.sent === true && !bareId(item.in_reply_to) && (item.references ?? []).length === 0
  );
}

/** Oldest first, and a stable tiebreak so two messages in one second do not swap. */
function order(a: Threadable, b: Threadable): number {
  return a.dateMs - b.dateMs || a.id.localeCompare(b.id);
}
